import { useState, useEffect, useCallback } from 'react';

const formFromExisting = (existingApp) => ({
  appName: existingApp?.appName || '',
  clientId: existingApp?.clientId || '',
  clientSecret: existingApp?.clientSecret || '',
  redirectUri: existingApp?.redirectUri || '',
});

/**
 * État et actions partagés pour le wizard Twitch / Spotify.
 *
 * @param {Object} options
 * @param {object|null} options.existingApp - App en édition ou null (création)
 * @param {() => Promise<string>} options.getSuggestedRedirectUri
 * @param {() => string} options.getFallbackSuggestedUri
 * @param {(data: object) => Promise<object>} options.createApp
 * @param {(data: object) => Promise<object>} options.updateApp
 * @param {(app: object) => void} options.onSuccess - Après création / mise à jour réussie
 */
export function useDeveloperAppSetup({
  existingApp,
  getSuggestedRedirectUri,
  getFallbackSuggestedUri,
  createApp,
  updateApp,
  onSuccess,
}) {
  const isEditMode = !!existingApp;

  const [step, setStep] = useState(isEditMode ? 2 : 1);
  const [formData, setFormData] = useState(() =>
    formFromExisting(existingApp)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copiedField, setCopiedField] = useState('');
  const [suggestedRedirectUri, setSuggestedRedirectUri] = useState('');
  const [isLoadingUri, setIsLoadingUri] = useState(true);

  useEffect(() => {
    setStep(existingApp ? 2 : 1);
    setFormData(formFromExisting(existingApp));
    setError('');
  }, [existingApp]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoadingUri(true);
        const uri = await getSuggestedRedirectUri();
        if (!cancelled) setSuggestedRedirectUri(uri);
      } catch {
        if (!cancelled) setSuggestedRedirectUri(getFallbackSuggestedUri());
      } finally {
        if (!cancelled) setIsLoadingUri(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Chargement unique au montage : en prod les fonctions viennent de la config (refs stables).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditMode || step !== 2 || !suggestedRedirectUri) return;
    setFormData((prev) => {
      if (prev.redirectUri.trim()) return prev;
      return { ...prev, redirectUri: suggestedRedirectUri };
    });
  }, [isEditMode, step, suggestedRedirectUri]);

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  }, []);

  const copyToClipboard = useCallback(async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Erreur lors de la copie:', err);
      }
    }
  }, []);

  const goToStep2 = useCallback(() => {
    setStep(2);
    setFormData((prev) => ({
      ...prev,
      redirectUri: prev.redirectUri.trim() || suggestedRedirectUri || prev.redirectUri,
    }));
  }, [suggestedRedirectUri]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError('');

      try {
        if (!formData.appName.trim()) {
          throw new Error("Le nom de l'application est requis");
        }
        if (!formData.clientId.trim()) {
          throw new Error('Le Client ID est requis');
        }
        if (!formData.clientSecret.trim()) {
          throw new Error('Le Client Secret est requis');
        }
        if (!formData.redirectUri.trim()) {
          throw new Error("L'URI de redirection est requise");
        }

        const app = isEditMode
          ? await updateApp(formData)
          : await createApp(formData);
        onSuccess(app);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, isEditMode, createApp, updateApp, onSuccess]
  );

  return {
    step,
    setStep,
    formData,
    handleInputChange,
    isSubmitting,
    error,
    copiedField,
    copyToClipboard,
    suggestedRedirectUri,
    isLoadingUri,
    isEditMode,
    goToStep2,
    handleSubmit,
  };
}

export { formFromExisting };
